import React, { useMemo, useState } from "react";
import { sortedStories, mapGroups, fmtDate } from "../../data/impactStories";
import { MapPin, X, ArrowUpRight } from "lucide-react";

// Stylised dot-matrix UK silhouette (22 x 31 grid, 10px cells)
const GRID = [
  "........####..........",
  "......#######.........",
  ".....########.........",
  "....##########........",
  "....##########........",
  ".....#########........",
  "....##########........",
  ".....########.........",
  "......######..........",
  ".....#######..........",
  "......######..........",
  ".......#####..........",
  "##......#####.........",
  "###.....######........",
  "###.....#######.......",
  "##......#######.......",
  "........########......",
  "........########......",
  ".......#########......",
  ".......##########.....",
  ".....############.....",
  "....##############....",
  "...###############....",
  "...################...",
  "....################..",
  ".....###############..",
  "...#################..",
  ".##################...",
  "..################....",
  "....######..#####.....",
  "......###....###......"
];

export default function UkMap({ onOpenStory }) {
  const [group, setGroup] = useState("All");
  const [activePin, setActivePin] = useState(null);

  const pins = useMemo(
    () => sortedStories.filter((s) => s.location && (group === "All" || s.location.group === group)),
    [group]
  );
  const active = sortedStories.find((s) => s.id === activePin);

  const dots = useMemo(() => {
    const d = [];
    GRID.forEach((row, r) => {
      row.split("").forEach((c, col) => {
        if (c === "#") d.push([col * 10 + 5, r * 10 + 5]);
      });
    });
    return d;
  }, []);

  return (
    <section id="map" data-testid="ri-uk-map" className="py-24 lg:py-32 bg-[#08080a] scroll-mt-20">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5 reveal">
            <div className="lg:sticky lg:top-32">
              <div className="text-[11px] uppercase tracking-[0.3em] text-amber-accent mb-4">Across the UK</div>
              <h2 className="font-display text-4xl sm:text-5xl font-bold leading-[1.05] mb-6">
                Impact, <span className="text-amber-accent">pinned.</span>
              </h2>
              <p className="text-white/60 leading-relaxed text-sm mb-8">
                Every Impact Story with a location lands on this map automatically. It grows as we do.
              </p>
              <div className="flex flex-wrap gap-2 mb-8" data-testid="map-filters">
                {mapGroups.map((g) => (
                  <button
                    key={g}
                    data-testid={`map-filter-${g.toLowerCase()}`}
                    onClick={() => { setGroup(g); setActivePin(null); }}
                    className={`px-3.5 py-1.5 rounded-full text-[11px] uppercase tracking-[0.16em] font-semibold border transition-all duration-300 ${
                      group === g ? "bg-[#F5D000] text-black border-[#F5D000]" : "text-white/55 border-white/15 hover:border-white/40 hover:text-white"
                    }`}
                  >
                    {g}
                  </button>
                ))}
              </div>

              {/* Pin detail panel */}
              {active ? (
                <div data-testid="map-pin-panel" className="ri-fade-up rounded-2xl bg-[#101014] border border-[#F5D000]/30 overflow-hidden">
                  <div className="relative h-44">
                    <img src={active.image} alt="" className="w-full h-full object-cover" />
                    <button
                      data-testid="map-panel-close"
                      onClick={() => setActivePin(null)}
                      className="absolute top-3 right-3 w-7 h-7 rounded-full bg-black/60 border border-white/20 flex items-center justify-center text-white/70 hover:text-white"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-[#F5D000] mb-1.5">
                      <MapPin className="w-3 h-3" /> {active.location.name}
                    </div>
                    <h3 className="font-display text-lg font-bold text-white leading-snug mb-2">{active.title}</h3>
                    <p className="text-xs text-white/60 leading-relaxed mb-3 line-clamp-3">{active.summary}</p>
                    <div className="text-[11px] text-white/45 mb-4">{active.client} · {fmtDate(active.date)}</div>
                    <button
                      data-testid="map-open-story"
                      onClick={() => onOpenStory(active)}
                      className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#F5D000] hover:gap-3 transition-all"
                    >
                      Full story <ArrowUpRight className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              ) : (
                <div className="rounded-2xl border border-dashed border-white/12 p-6 text-sm text-white/45">
                  Select a pin to see the story behind it.
                </div>
              )}
            </div>
          </div>

          {/* Map */}
          <div className="lg:col-span-7 reveal">
            <div className="relative rounded-2xl bg-[#0c0c0f] border border-white/8 p-6 sm:p-10">
              <svg viewBox="0 0 220 310" className="w-full max-w-[520px] mx-auto" data-testid="uk-dot-map">
                {dots.map(([x, y], i) => (
                  <circle key={i} cx={x} cy={y} r="3" fill="rgba(255,255,255,0.10)" />
                ))}
                {pins.map((s) => (
                  <g key={s.id} onClick={() => setActivePin(s.id)} className="cursor-pointer" data-testid={`map-pin-${s.id}`}>
                    <title>{s.location.name}, {s.title}</title>
                    <circle cx={s.location.x} cy={s.location.y} r="10" fill="rgba(245,208,0,0.15)" className="map-pin-pulse" />
                    <circle
                      cx={s.location.x} cy={s.location.y} r={activePin === s.id ? 6 : 4.5}
                      fill={activePin === s.id ? "#F5D000" : "#0a0a0a"}
                      stroke="#F5D000" strokeWidth="2"
                      style={{ transition: "r 0.3s ease, fill 0.3s ease" }}
                    />
                  </g>
                ))}
              </svg>
              <div className="mt-4 text-center text-[10px] uppercase tracking-[0.25em] text-white/35">
                {pins.length} {pins.length === 1 ? "story" : "stories"} · {group === "All" ? "all categories" : group}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
